import React, { useEffect, useRef, useState } from "react";

function formatNumber(value, decimals = 0) {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function AnimatedNumber({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 750,
}) {
  const numericValue = Number(value || 0);
  const [displayValue, setDisplayValue] = useState(numericValue);
  const previousValueRef = useRef(numericValue);

  useEffect(() => {
    const fromValue = previousValueRef.current;
    const toValue = numericValue;
    if (fromValue === toValue) {
      setDisplayValue(toValue);
      return undefined;
    }

    let frameId = 0;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / Math.max(1, duration));
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = fromValue + (toValue - fromValue) * eased;
      setDisplayValue(nextValue);
      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      } else {
        previousValueRef.current = toValue;
        setDisplayValue(toValue);
      }
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [duration, numericValue]);

  return (
    <span>
      {prefix}
      {formatNumber(displayValue, decimals)}
      {suffix}
    </span>
  );
}

export function PageHeader({ eyebrow = "", title, subtitle = "", actions = null }) {
  return (
    <div className="petaar-page-header">
      <div>
        {eyebrow ? <div className="petaar-eyebrow">{eyebrow}</div> : null}
        <h1 className="petaar-page-title">{title}</h1>
        {subtitle ? <p className="petaar-page-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{actions}</div> : null}
    </div>
  );
}

export function StatCard({
  icon,
  label,
  value,
  tone = "default",
  prefix = "",
  suffix = "",
  decimals = 0,
  meta = "",
}) {
  return (
    <div className="petaar-stat-card" data-tone={tone}>
      {icon ? <div className="petaar-stat-icon">{icon}</div> : null}
      <div className="petaar-stat-value">
        <AnimatedNumber value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </div>
      <div className="petaar-stat-label">{label}</div>
      {meta ? <div className="petaar-stat-meta">{meta}</div> : null}
    </div>
  );
}

export function ToneBadge({ tone = "default", children }) {
  return <span className={`petaar-badge petaar-badge--${tone}`}>{children}</span>;
}

export function EmptyState({
  icon = "🐾",
  title,
  description = "",
  actionLabel = "",
  onAction,
}) {
  return (
    <div className="petaar-empty-state petaar-card">
      <div className="petaar-empty-state-emoji">{icon}</div>
      <h3 style={{ fontSize: "1.5rem" }}>{title}</h3>
      {description ? (
        <p style={{ margin: 0, color: "var(--muted)", maxWidth: 520, lineHeight: 1.6 }}>
          {description}
        </p>
      ) : null}
      {actionLabel && typeof onAction === "function" ? (
        <button className="petaar-button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}
